import { ref } from "vue";
import { extractColors } from "extract-colors";

// Define the Color interface based on what extract-colors returns
export interface Color {
    hex: string;
    red: number;
    green: number;
    blue: number;
    hue: number;
    saturation: number;
    area: number;
    luminance?: number;
}

export interface ColorScheme {
    primaryColor: string;
    secondaryColor: string;
    primaryTextColor: string;
    secondaryTextColor: string;
    isDarkMode: boolean;
}

export function useColorExtraction() {
    const primaryColor = ref("#1976d2"); // Default MUI primary color
    const secondaryColor = ref("#9c27b0"); // Default MUI secondary color
    const primaryTextColor = ref("#ffffff"); // Default text color for primary
    const secondaryTextColor = ref("#ffffff"); // Default text color for secondary
    const isDarkMode = ref(false); // Default mode

    // Calculate Euclidean distance between two colors
    const colorDistance = (color1: Color, color2: Color): number => {
        const rDiff = color1.red - color2.red;
        const gDiff = color1.green - color2.green;
        const bDiff = color1.blue - color2.blue;
        const hDiff = color1.hue - color2.hue;
        const sDiff = color1.saturation - color2.saturation;

        // Weight hue and saturation differences more to get visually different colors
        return Math.sqrt(
            rDiff * rDiff +
                gDiff * gDiff +
                bDiff * bDiff +
                hDiff * hDiff * 100 +
                sDiff * sDiff * 100
        );
    };

    // Helper function to determine if a color is light or dark
    const isColorLight = (color: string): boolean => {
        // Convert hex to RGB
        const hex = color.replace("#", "");
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Calculate perceived brightness using the formula:
        // (0.299*R + 0.587*G + 0.114*B)
        const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        // Return true if the color is light (brightness > 0.5)
        return brightness > 0.5;
    };

    // Extract colors from image using extract-colors package
    const extractColorsFromImage = async (
        imageUrl: string
    ): Promise<ColorScheme> => {
        try {
            // Create an image element to use with extract-colors
            const img = new Image();
            img.crossOrigin = "Anonymous";

            // Set up a promise to handle image loading
            const imageLoadPromise = new Promise<HTMLImageElement>(
                (resolve, reject) => {
                    img.onload = () => resolve(img);
                    img.onerror = (e) => reject(e);
                    img.src = imageUrl;
                }
            );

            // Wait for image to load
            const loadedImg = await imageLoadPromise;

            // Use extract-colors to get the dominant colors
            // Options control the color extraction process
            const options = {
                pixels: 10000, // number of pixels to sample
                distance: 0.2, // color distance (higher = fewer colors)
                saturationImportance: 0.5, // weight of saturation in distance calculation
                splitPower: 8, // how many colors to extract
            };

            const colors = await extractColors(loadedImg, options);

            if (colors && colors.length > 0) {
                // Sort by area (largest first)
                colors.sort((a, b) => b.area - a.area);

                // Filter out colors with very low saturation (close to grayscale)
                const nonGrayscaleColors = colors.filter(
                    (color) => color.saturation > 0.1
                );

                if (nonGrayscaleColors.length > 0) {
                    // Use the most dominant non-grayscale color as primary
                    primaryColor.value = nonGrayscaleColors[0].hex;

                    // Find a contrasting color for secondary if available
                    if (nonGrayscaleColors.length > 1) {
                        // Find a color that's most different from the primary
                        const contrastingColors = [...nonGrayscaleColors].sort(
                            (a, b) => {
                                const distA = colorDistance(
                                    nonGrayscaleColors[0],
                                    a
                                );
                                const distB = colorDistance(
                                    nonGrayscaleColors[0],
                                    b
                                );
                                return distB - distA;
                            }
                        );

                        // Use the most contrasting color as secondary
                        secondaryColor.value =
                            contrastingColors[1]?.hex || secondaryColor.value;
                    }

                    // Determine appropriate text colors based on background brightness
                    primaryTextColor.value = isColorLight(primaryColor.value)
                        ? "#000000"
                        : "#ffffff";
                    secondaryTextColor.value = isColorLight(
                        secondaryColor.value
                    )
                        ? "#000000"
                        : "#ffffff";

                    // Determine if we should use dark mode based on primary color
                    isDarkMode.value = !isColorLight(primaryColor.value);

                    console.log("Extracted colors:", {
                        primary: primaryColor.value,
                        secondary: secondaryColor.value,
                        primaryText: primaryTextColor.value,
                        secondaryText: secondaryTextColor.value,
                        darkMode: isDarkMode.value,
                    });
                }
            }
        } catch (err) {
            console.error("Error extracting colors:", err);
            // Keep default values on error
        }

        return {
            primaryColor: primaryColor.value,
            secondaryColor: secondaryColor.value,
            primaryTextColor: primaryTextColor.value,
            secondaryTextColor: secondaryTextColor.value,
            isDarkMode: isDarkMode.value,
        };
    };

    // Reset to default colors
    const resetColors = () => {
        primaryColor.value = "#1976d2";
        secondaryColor.value = "#9c27b0";
        primaryTextColor.value = "#ffffff";
        secondaryTextColor.value = "#ffffff";
        isDarkMode.value = false;
    };

    return {
        primaryColor,
        secondaryColor,
        primaryTextColor,
        secondaryTextColor,
        isDarkMode,
        extractColorsFromImage,
        resetColors,
        isColorLight,
    };
}
